/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { CombinedGraphQLErrors, type ErrorLike } from "@apollo/client";
import { isApiError } from "@ndla/shared/apiClient";
import type { LoggerContext } from "@ndla/shared/server";
import { captureException, setContext } from "@sentry/react";
import type { GraphQLFormattedError } from "graphql";
import { type ErrorResponse, isRouteErrorResponse } from "react-router";
import config from "../config";
import type { LogLevel } from "../interfaces";
import { FORBIDDEN, GONE, NOT_FOUND, UNAUTHORIZED } from "../statusCodes";
import { NDLAError } from "./error/NDLAError";
import { StatusError } from "./error/StatusError";
import { unreachable } from "./guards";
import { getLoggerContext } from "./logger/getLoggerContext";
import { log } from "./logger/logger";

type UnknownError = {
  status?: number;
};

const getErrorStatuses = (error: unknown): number[] => {
  const statuses: number[] = [];
  if (error == null) return statuses;
  const unknownError = error as UnknownError;
  if (unknownError.status) {
    statuses.push(unknownError.status);
  } else if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((e) => {
      const unknownError = e as UnknownError;
      if (unknownError.status) {
        statuses.push(unknownError.status);
        return;
      }
      if (typeof e.extensions?.status === "number") {
        statuses.push(e.extensions.status);
      }
    });
  }
  return statuses;
};

export const AccessDeniedCodes = [UNAUTHORIZED, FORBIDDEN];

export const InternalServerErrorCodes = [500, 503, 504];

const hasStatus = (error: ErrorLike | undefined | null, errorCodes: number[]): boolean =>
  getErrorStatuses(error).some((status) => errorCodes.includes(status));

export const hasAccessDeniedStatus = (error: ErrorLike | undefined | null) => hasStatus(error, AccessDeniedCodes);

export const findAccessDeniedErrors = (error: ErrorLike | undefined | null): GraphQLFormattedError[] => {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.filter((err) => {
      // not sure if `err.status` ever exists
      const code = (err as any).status ?? err.extensions?.status;
      return AccessDeniedCodes.includes(code ?? 0);
    });
  }
  return [];
};

export const hasNotFoundStatus = (error: ErrorLike | undefined | null) => hasStatus(error, [NOT_FOUND]);

export const hasGoneStatus = (error: ErrorLike | undefined | null) => hasStatus(error, [GONE]);

const getMessage = (error: Error | unknown): string => {
  if (error instanceof StatusError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  if (error instanceof AggregateError) {
    const aggregateMessages = error.errors.map((e) => {
      const message = getMessage(e);
      const stack = e.stack ? `Stack: ${e.stack}` : "";
      return `${message} ${stack}`;
    });
    return `AggregateError with errors: [${aggregateMessages}]`;
  }
  if (typeof error === "string" && error) return error;
  return "Got error without message";
};

const getStatus = (extraContext: object | undefined, error: Error | unknown): number | undefined => {
  if (extraContext && "statusCode" in extraContext && typeof extraContext.statusCode === "number")
    return extraContext.statusCode;
  if (error instanceof StatusError) return error.status;
  if (error instanceof Error && "status" in error && typeof error.status === "number") {
    return error.status;
  }
  return undefined;
};

// Node/undici expose the actual failure reason as own (often non-enumerable) properties that
// `JSON.stringify` would otherwise drop. These turn a bare "fetch failed" into something diagnosable.
const ERROR_DETAIL_KEYS = [
  "code",
  "errno",
  "syscall",
  "address",
  "port",
  "hostname",
  "requestUrl",
  "requestMethod",
] as const;

type ErrorDetailKey = (typeof ERROR_DETAIL_KEYS)[number];
type ErrorDetails = Partial<Record<ErrorDetailKey, unknown>>;
type ErrorWithMaybeDetails = Error & ErrorDetails;

const pickErrorDetails = (error: ErrorWithMaybeDetails): Record<string, unknown> => {
  const details: ErrorDetails = {};
  for (const key of ERROR_DETAIL_KEYS) {
    const value = error[key];
    if (value !== undefined) details[key] = value;
  }
  return details;
};

const MAX_CAUSE_DEPTH = 5;

/** undici hides the real failure behind a generic "fetch failed" `TypeError`, with the reason
 * (ENOTFOUND, ECONNREFUSED, timeouts, TLS errors ...) living in `error.cause` — frequently an
 * `AggregateError` whose `errors` hold the per-address details. Recursively serialise that chain so
 * the logs actually say what went wrong. */
const serializeCause = (error: unknown, depth = 0): unknown => {
  if (error == null || depth > MAX_CAUSE_DEPTH) return undefined;
  if (!(error instanceof Error)) {
    // objects are returned as-is just below, so this only ever stringifies a primitive
    // oxlint-disable-next-line typescript/no-base-to-string
    if (typeof error !== "object") return String(error);
    return error;
  }
  const result: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    ...pickErrorDetails(error),
  };
  if (error instanceof AggregateError && Array.isArray(error.errors)) {
    result.errors = error.errors.map((e) => serializeCause(e, depth + 1));
  }
  const cause = serializeCause(error.cause, depth + 1);
  if (cause !== undefined) result.cause = cause;
  return result;
};

export const getErrorLog = (error: ErrorLike | unknown, extraContext: object | undefined): object | string => {
  const ctx: Record<string, unknown> = {
    ...extraContext,
    statusCode: getStatus(extraContext, error),
  };
  if (!error) return { ...ctx, message: `Unknown error: ${JSON.stringify(error)}` };

  const withCause = (base: Record<string, unknown>, err: Error): Record<string, unknown> => {
    const cause = serializeCause(err.cause ?? ctx.cause);
    if (cause !== undefined) base.cause = cause;
    return base;
  };

  if (error instanceof StatusError || isApiError(error)) {
    return withCause(
      {
        ...ctx,
        message: getMessage(error),
        json: error.json,
        status: error.status,
        stack: error.stack,
        name: error.name,
        ...pickErrorDetails(error),
      },
      error,
    );
  }

  if (error instanceof Error) {
    return withCause(
      {
        ...ctx,
        message: getMessage(error),
        stack: error.stack,
        name: error.name,
        ...pickErrorDetails(error),
      },
      error,
    );
  }

  if (typeof error === "object") {
    return { ...error, ...ctx, message: getMessage(error) };
  }

  if (typeof error === "string") {
    return { ...ctx, message: getMessage(error) };
  }

  return error;
};

export const getLogLevelFromStatusCode = (statusCode: number): LogLevel => {
  if ([401, 403, 404, 410].includes(statusCode)) return "info";
  if (statusCode < 500) return "warn";
  return "error";
};

export const mergeLogLevels = (levels: LogLevel[]): LogLevel | undefined => {
  if (levels.length === 0) return undefined;
  if (levels.includes("error")) return "error";
  if (levels.includes("warn")) return "warn";
  return "info";
};

export const deriveLogLevel = (error: Error | unknown): LogLevel | undefined => {
  if (error instanceof NDLAError) return error.logLevel;

  const statusCodes = getErrorStatuses(error);
  const logLevels = statusCodes.map((sc) => getLogLevelFromStatusCode(sc));
  return mergeLogLevels(logLevels);
};

const deriveContext = (error: Error): Record<string, unknown> => {
  if (error instanceof NDLAError) {
    return error.logContext;
  }
  return {};
};

const logServerError = async (error: Error, extraContext: Record<string, unknown>) => {
  const derivedContext = deriveContext(error);
  const ctx = { ...extraContext, ...derivedContext };
  const logLevel = deriveLogLevel(error);
  const err = getErrorLog(error, ctx);
  switch (logLevel) {
    case "info":
      log.info(err);
      break;
    case "warn":
      log.warn(err);
      break;
    case "error":
    case undefined:
      log.error(err);
      break;
    default:
      unreachable(logLevel);
  }
};

const sendToSentry = (
  error: Error,
  loggerContext: LoggerContext | undefined,
  extraContext: Record<string, unknown>,
) => {
  const errorContext = { error, ...loggerContext, ...extraContext };
  setContext("NDLA Context", errorContext);
  captureException(error);
};

const fromRouteErrorResponse = (response: ErrorResponse): StatusError => {
  const internalError = "error" in response && response.error instanceof Error ? response.error : undefined;
  const message =
    internalError?.message ||
    (typeof response.data === "string" && response.data) ||
    `${response.status} ${response.statusText}`;
  const error = new StatusError(message, response.status);
  // A 4xx route error response is caused by the request itself (e.g. a POST to a route without an action)
  error.logLevel = response.status < 500 ? "info" : "error";
  return error;
};

export const ensureError = (unknownError: ErrorLike | unknown): ErrorLike => {
  if (unknownError instanceof Error) return unknownError;
  if (isRouteErrorResponse(unknownError)) return fromRouteErrorResponse(unknownError);
  return new NDLAError(String(unknownError));
};

export const handleError = async (error: ErrorLike, extraContext: Record<string, unknown> = {}) => {
  if (import.meta.env.SSR) {
    await logServerError(error, extraContext);
  } else {
    if (import.meta.env.PROD && config.enableSentry) {
      const ctx = await getLoggerContext();
      sendToSentry(error, ctx, extraContext);
    }

    console.error(error); // oxlint-disable-line no-console
  }
};
